import { useState } from 'react'
import PageMainTitle from 'components/commons/PageMainTitle'
import { pageMainTitle } from 'components/topics/TitleGroup/TitleGroup'
import styles from '../../Topics.module.scss'
import Link from 'next/link'
import Pagination from 'components/topics/Pagination'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import CaseStudy from 'components/home/CaseStudy'
import EventSlider from 'components/topics/EventSlider'
import Button from 'components/commons/Button/Button'
import MailmagazineLink from 'components/mailmagazine/MailmagazineLink/MailmagazineLink'
gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TimelineVars = [
  {
    y: 50,
    duration: 1,
    opacity: 0,
    ease: Power4.easeOut,
  },
  {
    y: 100,
    duration: 1.2,
    opacity: 0,
    ease: Power4.easeOut,
  },
  { duration: 0, width: 0, ease: Power4.easeOut, border: '0 #ffff' },
  { duration: 2, width: '100%', border: '0.1px solid #eeeeee' },
]

const TopicsEvents = ({ posts, page, totalPageNumber, activeEvents }) => {
  const pageId = parseInt(page)
  const currentPage = pageId
  const [totalPages] = useState(totalPageNumber)

  const statusToClassMap = {
    赤: 'statusIcon_red',
    赤枠: 'statusIcon_red_framed',
    青: 'statusIcon_blue',
    青枠: 'statusIcon_blue_framed',
    ピンク: 'statusIcon_pink',
    ピンク枠: 'statusIcon_pink_framed',
    オレンジ: 'statusIcon_orange',
    オレンジ枠: 'statusIcon_orange_framed',
    緑: 'statusIcon_green',
    緑枠: 'statusIcon_green_framed',
    紫: 'statusIcon_purple',
    紫枠: 'statusIcon_purple_framed',
    グレー: 'statusIcon_gray',
    グレー枠: 'statusIcon_gray_framed',
    薄グレー: 'statusIcon_light_gray',
    白: 'statusIcon_white',
  }

  const getStatusClass = (status) => {
    const baseClass = `${styles.status_day} ${styles.eventDay} ${styles.statusIcon}`
    const specificClassName = statusToClassMap[status] || ''
    const specificClass = specificClassName ? styles[specificClassName] || '' : ''
    const fullClass = specificClass ? `${baseClass} ${specificClass}` : baseClass
    return fullClass.trim()
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土']
    const dayOfWeek = daysOfWeek[date.getDay()]
    return {
      fullDate: `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${dayOfWeek}）`,
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      dayOfWeek: dayOfWeek,
    }
  }

  const getEventDateDisplay = (post) => {
    const startInfo = post.start_date ? formatDate(post.start_date) : null
    const endInfo = post.end_date ? formatDate(post.end_date) : null

    if (startInfo && endInfo) {
      if (startInfo.fullDate === endInfo.fullDate) {
        return startInfo.fullDate
      } else if (startInfo.year === endInfo.year) {
        if (startInfo.month === endInfo.month) {
          return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.day}日（${endInfo.dayOfWeek}）`
        } else {
          return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.month}月${endInfo.day}日（${endInfo.dayOfWeek}）`
        }
      } else {
        return `${startInfo.fullDate}〜${endInfo.fullDate}`
      }
    } else {
      return startInfo?.fullDate || endInfo?.fullDate || ''
    }
  }

  const getPageLink = (pageNo) => {
    return `/events/page/${pageNo}`
  }

  const paths = [
    { name: 'TOP', url: '/' },
    { name: 'Topics', url: '/pressrelease/page/1' },
    { name: 'Event/Seminar', url: '#' },
  ]

  const list = []
  for (let i = 0; i < posts.length; i++) {
    list.push(
      <Link
        href={{ pathname: `/events/${posts[i].id}`, query: { page: currentPage } }}
        as={`/events/${posts[i].id}`}
        key={posts[i].id}
      >
        <a className={styles.list_wrapper}>
          <div className={styles.flexlist}>
            <figure className={styles.flexlist_thumbnail}>
              <img
                src={posts[i].thumbnail_img || '/images/img/img_thumbnail_dummy.png'}
                alt={posts[i].title.rendered || 'デフォルト画像'}
              />
            </figure>
            <div className={styles.listcontent}>
              {posts[i].start_date && (
                <div className={styles.status}>
                  {posts[i].status01 && (
                    <span className={getStatusClass(posts[i].status01_color)}>{posts[i].status01}</span>
                  )}
                  {posts[i].status02 && (
                    <span className={getStatusClass(posts[i].status02_color)}>{posts[i].status02}</span>
                  )}
                  <span className={`${styles.status_day} ${styles.eventDay}`}>
                    {getEventDateDisplay(posts[i])} 開催
                  </span>
                </div>
              )}
              <div className={styles.textWrap}>
                <p className={styles.topic_title}>{posts[i].title.rendered}</p>
                {/* <span className={`${styles.topic_arrow} ${styles.is_pc}`}></span> */}
              </div>
              <span className={`${styles.topic_arrow}`}></span>
            </div>
          </div>
        </a>
      </Link>,
    )
  }

  const timeline = gsap.timeline()
  const contents = useRef<HTMLDivElement>()
  const button = useRef<HTMLDivElement>()

  useEffect(() => {
    Array.prototype.forEach.call(contents.current.children, (element: HTMLElement, index: number) => {
      timeline
        .from(element.children[0], tweenVars[0], 1 + index * 0.3)
        .fromTo(element.children[1], tweenVars[2], tweenVars[3], 1.2 + index * 0.3)
    })
    timeline.from(button.current, tweenVars[0], 1.5)

    ScrollTrigger.create({
      animation: timeline,
      trigger: contents.current,
      start: 'top-=250 80%',
      toggleActions: 'play none none reverse',
    })
  }, [])

  return (
    <section>
      <PageMainTitle pageMainTitle={pageMainTitle} paths={paths} />
      <EventSlider activeEvents={activeEvents} />
      <div className={styles.content} id="paginationTarget">
        <div className={styles.news_group}>
          <ul className={styles.tab}>
            <Link href="/pressrelease/page/1" scroll={false}>
              <li className={styles.category}>
                <a>Press release</a>
              </li>
            </Link>
            <Link href="/news/page/1" scroll={false}>
              <li className={styles.category}>
                <a>News</a>
              </li>
            </Link>
            <li className={styles.currentCategory}>
              <a>Event/Seminar</a>
            </li>
          </ul>
          <div ref={contents}>{list}</div>
          <div className={styles.pagination}>
            <Pagination currentPage={currentPage} totalPages={totalPages} getPageLink={getPageLink} />
          </div>
        </div>
        <div className={styles.mailmagazine_link}>
          <p>
            UI/UXデザイン・システム内製化・DX推進についてのお役立ちメソッドや、限定イベントや最新事例などをお届けします。
          </p>
          <div className={styles.mailmagazine_link_button}>
            <Button text="メールマガジン" href="/mailmagazine" red="red" />
          </div>
        </div>
      </div>
      <CaseStudy tagType="h2" />
      <MailmagazineLink />
    </section>
  )
}

export default TopicsEvents
